// Google Analytics 4 wiring.
//
// The tag itself is bootstrapped in public/index.html so it is present on every
// page before React runs — including the static shell a visitor sees while the
// map chunk loads. This module owns everything that happens afterwards: the
// consent choice, and the page_view events that a single-page app has to send
// for itself.

/** GA4 property for centmap.app. Mirrored in public/index.html. */
export const GA_MEASUREMENT_ID = 'G-VLM4M92PWP';

/**
 * Where the visitor's choice is kept. Read synchronously by the inline
 * bootstrap in public/index.html so a returning visitor's first hit already
 * carries their consent — **keep the two in sync**.
 */
export const CONSENT_STORAGE_KEY = 'centmap_analytics_consent';

export type ConsentChoice = 'granted' | 'denied';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Timezones we treat as "consent required".
 *
 * Two independent mechanisms decide what actually happens, and only this one is
 * about the *banner*: whether to ask. Whether analytics storage is allowed is
 * settled by the region-scoped Consent Mode default in index.html, which Google
 * resolves from the visitor's IP and which we cannot fool from here.
 *
 * So this list is deliberately over-inclusive — Europe/Moscow and
 * Europe/Istanbul are outside the EEA but get asked anyway, which costs
 * nothing. The reverse error is the one that matters, and it fails safe: a
 * visitor we fail to recognise as European is still denied storage by the
 * IP-scoped default; they simply never get the chance to opt in.
 */
const CONSENT_REQUIRED_ZONES = [
  'Europe/',
  // EEA territory that sits outside the Europe/ prefix.
  'Atlantic/Reykjavik', // Iceland
  'Atlantic/Canary',    // Spain
  'Atlantic/Madeira',   // Portugal
  'Atlantic/Azores',    // Portugal
  'Atlantic/Faroe',     // Denmark
  'Arctic/Longyearbyen',// Norway
  // French outermost regions — EU law applies in full.
  'America/Martinique',
  'America/Guadeloupe',
  'America/Cayenne',
  'Indian/Reunion',
  'Indian/Mayotte',
];

/**
 * Whether this visitor is somewhere consent has to be asked for.
 *
 * Uses the browser's own timezone rather than a geo-IP lookup: it is instant,
 * needs no third-party request, and asks the visitor for nothing. If the
 * browser will not tell us, we assume consent is required.
 */
export function isConsentRequiredRegion(): boolean {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!zone) return true;
    return CONSENT_REQUIRED_ZONES.some((prefix) => zone.startsWith(prefix));
  } catch {
    return true;
  }
}

/** The stored choice, or null if this visitor has not answered yet. */
export function getStoredConsent(): ConsentChoice | null {
  try {
    const stored = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return stored === 'granted' || stored === 'denied' ? stored : null;
  } catch {
    // Safari in private mode throws on localStorage access.
    return null;
  }
}

/**
 * Record a choice and tell the tag about it.
 *
 * Ad storage stays denied in both branches: Centmap runs no advertising, and
 * consenting to analytics is not consent to anything else.
 */
export function setConsent(choice: ConsentChoice): void {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch {
    // Nothing persisted, so they will be asked again next visit — which is the
    // right way for this to fail.
  }

  window.gtag?.('consent', 'update', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: choice,
  });
}

/** Forget the stored choice, so the banner asks again. */
export function clearConsent(): void {
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}

/**
 * Send one event.
 *
 * Silent when the tag has not loaded — an ad blocker, or a network that never
 * reached Google. Nothing in the app should behave differently for it.
 */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  window.gtag?.('event', name, { ...params, send_to: GA_MEASUREMENT_ID });
}

/**
 * Send one page_view.
 *
 * `send_page_view: false` on the config in index.html leaves every pageview to
 * this function, including the landing one — otherwise the first page of every
 * visit would be counted twice, once by the tag and once by the router effect.
 */
export function trackPageView(path: string): void {
  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
    send_to: GA_MEASUREMENT_ID,
  });
}
